// Coupon shapes shared by the picker UI and POST /api/premium/validate-coupon.
//
// Mirrors the agent-coupon `Discount` oneof (wix.agent_premiums.v1.Discount). Every
// catalog discount is FIRST-BILLING-PERIOD ONLY (Stripe `duration=once`, or a bounded
// trial for free-time) — see ADR-029. Renewals are always full price, which is why the
// UI describes a first-period saving rather than restating the headline price.

import { minorToMajorUnits } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { Cadence, PickerOption } from './plans';
import { amountFor } from './money';
import { API_BASE } from './api-base';

export type CouponDiscount =
  | { kind: 'percentage'; percent: number }
  | { kind: 'fixedAmount'; amountMinor: number; currency: string }
  | { kind: 'freeTime'; months: number };

/** Why a code was turned down. Business rejections only — infra failures are HTTP errors. */
export type CouponRejection =
  | 'invalid'
  | 'expired'
  | 'wrong_plan'
  | 'wrong_cadence'
  | 'currency_mismatch'
  | 'already_used';

export type ValidateCouponResult =
  | { valid: true; code: string; discount: CouponDiscount }
  | { valid: false; reason: CouponRejection };

/** A validated code the buyer has applied to one card, held until they change the target. */
export interface AppliedCoupon {
  code: string;
  discount: CouponDiscount;
}

/**
 * What the buyer actually pays for the FIRST period, and what they pay after it.
 * `freeMonths` is set instead of `firstPeriodAmount` for a free-time coupon (no price to show —
 * the first N months simply aren't charged).
 */
export interface DiscountPreview {
  firstPeriodAmount?: number;
  freeMonths?: number;
  recurringAmount: number;
}

/**
 * Apply a validated discount to the option's price for the selected cadence. Returns null when the
 * cadence has no resolved price, or when a fixed-amount coupon is denominated in another currency
 * (the server rejects that too — this is a client-side backstop so we never render a bogus saving).
 * The discounted total is clamped at 0 and rounded to whole minor units.
 */
export function previewDiscount(
  discount: CouponDiscount,
  option: PickerOption,
  cadence: Cadence,
  currency: string | undefined,
): DiscountPreview | null {
  const full = amountFor(option, cadence);
  if (full == null) return null;

  switch (discount.kind) {
    case 'percentage':
      return { firstPeriodAmount: round2(full * (1 - discount.percent / 100)), recurringAmount: full };
    case 'fixedAmount': {
      if (currency && discount.currency.toUpperCase() !== currency.toUpperCase()) return null;
      // The coupon's own currency decides the divisor, not a constant 100 — a ¥500-off coupon carries
      // `amountMinor: 500`, and /100 would preview it as ¥5 off. Currencies are already asserted equal
      // above, so either side would do; the coupon's is the one the amount belongs to.
      const off = minorToMajorUnits(discount.amountMinor, discount.currency) ?? 0;
      return { firstPeriodAmount: Math.max(0, round2(full - off)), recurringAmount: full };
    }
    case 'freeTime':
      // KNOWN, ACCEPTED DEVIATION: agent-payments turns N free MONTHS into a Stripe trial of
      // `months * 30` DAYS (`StripeClient.trialDays`, flagged as a follow-up on their side). So
      // "your first month is free" is up to a day short in a 31-day month. Deliberately not
      // papered over here — the copy should track how coupons are authored (in months), and the
      // fix belongs upstream in a calendar-accurate `trial_end`.
      return { freeMonths: discount.months, recurringAmount: full };
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Client check that a coupon would be accepted for a (plan, cadence) target — the SAME
 * `/api/premium/validate-coupon` the promo field uses, reduced to the yes/no an auto-apply needs. ANY
 * non-valid result, non-2xx, or network/parse error resolves to FALSE, so a caller gating an
 * auto-applied coupon on it drops the coupon (full price) rather than blocking checkout. Not a
 * substitute for the server's re-validation at `CreateCheckoutSession` — it is the pre-check that lets
 * the auto coupon fall through seamlessly.
 */
export async function validateCouponCode(code: string, agentPlanId: string, cadence: Cadence): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/premium/validate-coupon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, agentPlanId, cadence }),
    });
    if (!res.ok) return false;
    const result = (await res.json()) as ValidateCouponResult;
    return result.valid === true;
  } catch {
    return false;
  }
}
